import { DATA_ATTR_THEME_MODE, ELEVATED_CONTAINER_CLASSNAME } from './consts';
import { unsafeObjectEntries, unsafeObjectFromEntries, unsafeObjectKeys } from './objectUtils';
import type { ThemedValue, ThemedValueStrict } from './types';

const COOKBOOK_ELEVATED_CONTAINER_CLASSNAME = 'cb-elevated'; // for Cookbook compatibility only

const toKebabCase = (str: string): string =>
  str
    .replaceAll(/([a-z])([A-Z])/g, '$1-$2')
    .replaceAll(/^-+|-+$/g, '') // Remove dashes from the start and end of the string
    .toLowerCase();

type CssVarConfig<TKey extends string> = Record<TKey, string>;

type CssVarConfigThemed<TKey extends string> = Record<TKey, ThemedValue | string>;

interface CssVarOptions {
  /**
   * @default false
   *
   * If true, converts the key to kebab-case.
   */
  kebabCase?: boolean;
  prefix?: string;
}

type Selector = '&' | '&&' | ':root';

function keyToCssVarName(key: string, options?: CssVarOptions): string {
  const keyName = options?.prefix ? `${options.prefix}-${key}` : key;
  return `--${options?.kebabCase ? toKebabCase(keyName) : keyName}`;
}

/**
 * Creates CSS variable definitions to be inserted in CSS.
 *
 * @example
 *   Input: `{ background: 'blue', text: 'black' }`;
 *   Output: `'--background: blue; --text: black;'`;
 */
export function createCssVarDefinitions<TVarKey extends string>(
  varMap: CssVarConfig<TVarKey>,
  options?: CssVarOptions,
) {
  return unsafeObjectEntries(varMap)
    .map(([key, value]) => `${keyToCssVarName(key, options)}: ${value};`)
    .join('\n');
}

/**
 * Creates an object map of CSS variables.
 *
 * @example
 *   Input: `['background', 'text']`;
 *   Output: `{ background: 'var(--background)', text: 'var(--text)' }`;
 */
export function createCssVars<TKey extends string>(
  varConfig: TKey[],
  options?: CssVarOptions,
): Record<TKey, string> {
  const varMap = Object.fromEntries(
    varConfig.map((key) => {
      const val = `var(${keyToCssVarName(key, options)})`;
      return [key, options?.kebabCase ? toKebabCase(val) : val];
    }),
  );

  Object.freeze(varMap);

  return varMap as Record<TKey, string>;
}

/**
 * Creates definitions for given CSS var configuration with `data-al-theme` based theme selectors.
 */
export function createThemedCssVarDefinitions<TKey extends string>(
  varMap: CssVarConfigThemed<TKey>,
  options?: CssVarOptions & {
    /**
     * '&' and '&&' creates a selector that targets the element itself. You may use it to create CSS
     * variables scoped to the element.
     *
     * `:root` Creates global CSS variables.
     *
     * @default '&'
     */
    selector?: Selector;
  },
): string {
  const { kebabCase, prefix, selector = '&' } = options ?? {};

  const { darkModeElevatedVars, darkModeVars, lightHighContrastVars, unscopedVars } =
    unsafeObjectEntries(varMap).reduce(
      (acc, [key, value]) => {
        if (typeof value === 'string') {
          acc.unscopedVars[key] = value;
          return acc;
        }

        acc.unscopedVars[key] = value.light;

        if (value.lightHighContrast && value.lightHighContrast !== value.light) {
          acc.lightHighContrastVars[key] = value.lightHighContrast;
        }

        if (typeof value.dark === 'object') {
          if (value.dark.default !== value.light) {
            acc.darkModeVars[key] = value.dark.default;
          }
          acc.darkModeElevatedVars[key] = value.dark.elevated;
        } else if (typeof value.dark === 'string' && value.dark !== value.light) {
          acc.darkModeVars[key] = value.dark;
        }

        return acc;
      },
      {
        unscopedVars: {},
        lightHighContrastVars: {},
        darkModeVars: {},
        darkModeElevatedVars: {},
      } as Record<
        'unscopedVars' | 'lightHighContrastVars' | 'darkModeVars' | 'darkModeElevatedVars',
        Record<TKey, string>
      >,
    );

  const unscopedCss = createCssVarDefinitions(unscopedVars, { prefix, kebabCase });
  const lightHighContrastCss = createCssVarDefinitions(lightHighContrastVars, {
    prefix,
    kebabCase,
  });
  const darkCss = createCssVarDefinitions(darkModeVars, { prefix, kebabCase });
  const darkElevatedCss = createCssVarDefinitions(darkModeElevatedVars, { prefix, kebabCase });

  // The root selector was changed to `html` because styled-components@5 does not work with `:root`.
  const rootSelector = selector === ':root' ? ':root' : 'html';

  return [
    selector === ':root' && unscopedCss && `:root{${unscopedCss}}`,
    selector !== ':root' && unscopedCss,
    !!lightHighContrastCss &&
      `${rootSelector}:where([${DATA_ATTR_THEME_MODE}="lightHighContrast"]) ${selector === ':root' ? '' : selector} {${lightHighContrastCss}}`,
    !!darkCss &&
      `${rootSelector}:where([${DATA_ATTR_THEME_MODE}="dark"]) ${selector === ':root' ? '' : selector} {${darkCss}}`,
    !!darkElevatedCss &&
      `${rootSelector}:where([${DATA_ATTR_THEME_MODE}="dark"]) :where(.${ELEVATED_CONTAINER_CLASSNAME}, .${COOKBOOK_ELEVATED_CONTAINER_CLASSNAME}) ${selector === ':root' ? '' : selector} {${darkElevatedCss}}`,
  ]
    .filter(Boolean)
    .join('\n\n');
}

/** Creates CSS var definitions for combinations of themes and data attributes. */
export function createAttributeCssVarDefinitions<TOptionKey extends string, TVarKey extends string>(
  attrName: string,
  params: {
    /** Additional CSS content to add for each variant. */
    extraCss?: Partial<Record<TOptionKey, string>>;
    /** Used as type contract */
    options: readonly TOptionKey[];
    /** Values for the CSS variables to generate for each variant */
    values: Record<TOptionKey, Record<TVarKey, ThemedValue | string>>;
    /** Used as type contract */
    varContract: Record<TVarKey, unknown>;
  },
  options?: CssVarOptions,
) {
  return unsafeObjectEntries(params.values)
    .map(
      ([key, val]) =>
        `&[${attrName}='${key}'] {
          ${createThemedCssVarDefinitions(val, options)}
          ${params.extraCss?.[key] ?? ''}
        }`,
    )
    .join('\n');
}

/** A utility to create static CSS variables and the CSS declarations for them. */
export function createThemedCssVarsAndDefinitions<TVarKey extends string>(
  varMap: CssVarConfigThemed<TVarKey>,
  options?: CssVarOptions & {
    /** @default '&' */
    selector?: Selector;
  },
) {
  return {
    vars: createCssVars(unsafeObjectKeys(varMap), options),
    definitions: createThemedCssVarDefinitions(varMap, options),
  };
}

/**
 * @example
 *   Input: `{ light: { bg, color }, dark: { bg, color } }`;
 *   Output: `{ bg: { light, lightHighContrast, dark }, color: { light, lightHighContrast, dark } }`;
 */
export function themedConfigToThemedAttrs<TKey extends string>(variantConfig: {
  dark: Record<TKey, string>;
  light: Record<TKey, string>;
  lightHighContrast?: Record<TKey, string>;
}): Record<TKey, ThemedValueStrict> {
  return unsafeObjectFromEntries(
    unsafeObjectKeys(variantConfig.light).map((key) => [
      key,
      {
        light: variantConfig.light[key],
        lightHighContrast: variantConfig.lightHighContrast?.[key] ?? variantConfig.light[key],
        dark: variantConfig.dark[key],
      },
    ]),
  );
}

/**
 * @example
 *   Input: `{ background: null }, { background: 'blue' }`;
 *   Output: `{ --background: 'blue' }`;
 */
export function assignInlineCssVars<TVarKey extends string>(
  _variables: Record<TVarKey, string | null>, // used as type contract
  values: Partial<Record<TVarKey, string>>,
): Partial<Record<`--${TVarKey}`, string>> {
  const cssVars = {} as Partial<Record<`--${TVarKey}`, string>>;

  for (const prop in values) {
    cssVars[`--${prop}`] = values[prop] as string;
  }

  return cssVars;
}
